import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { n, Status } from "@/components/stock/sku/SkuParts";
import ItemDetailsTab from "@/components/stock/sku/ItemDetailsTab";
import StockDetailsTab from "@/components/stock/sku/StockDetailsTab";
import WarehousesTab from "@/components/stock/sku/WarehousesTab";
import PartyReportTab from "@/components/stock/sku/PartyReportTab";
import PartyPricesTab from "@/components/stock/sku/PartyPricesTab";
import OperationsTab from "@/components/stock/sku/OperationsTab";
import EditItemDialog from "@/components/stock/sku/EditItemDialog";
import { AdjustDialog, TransferDialog, PutawayDialog, ArchiveDialog } from "@/components/stock/sku/SkuActionDialogs";

const TABS = ["Item Details", "Stock Details", "Warehouses / Godowns / Factories", "Party-wise Report", "Party-wise Prices", "Orders · Purchase · Production · QC"];
const HEAD = [["physical", "Physical Stock"], ["free", "Available", "text-signal-green"], ["reserved", "Reserved", "text-signal-amber"], ["unlocated", "Unlocated", "text-signal-amber"], ["hold", "QC Hold", "text-signal-red"], ["damaged", "Damaged", "text-signal-red"]];

export default function SkuDetail() {
  const { sku } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState(TABS[0]);
  const [dlg, setDlg] = useState(null);

  const load = useCallback(() => {
    api.get(`/stock/sku/${encodeURIComponent(sku)}/center`).then((r) => { setD(r.data); setErr(null); })
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load SKU"));
  }, [sku]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="text-signal-red text-sm" data-testid="sku-detail-error">{err}</div>;
  if (!d) return <p className="text-sm text-muted-foreground">Loading {sku}…</p>;
  const p = d.product, s = d.stock, perms = d.perms;
  const actions = [
    perms.adjust_stock && ["Adjust Stock", "adjust", "bg-primary text-primary-foreground"],
    perms.edit_item && ["Edit Item", "edit", ""],
    perms.transfer_stock && d.bins.length > 0 && ["Transfer Stock", "transfer", ""],
    perms.putaway && s.unlocated > 0 && ["Complete Putaway", "putaway", "bg-signal-amber text-black"],
    perms.view_ledger && ["View Ledger", "ledger", ""],
    (perms.archive_item || perms.delete_item) && [p.status === "archived" ? "Reactivate" : "Delete / Archive", "archive", "text-signal-red border-signal-red/40"],
  ].filter(Boolean);

  return (
    <div className="space-y-4" data-testid="sku-detail-page">
      <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground"><Link to="/stock" className="text-primary underline" data-testid="back-to-stock">Stock & Reservations</Link><span>›</span><span>SKU Control Center</span></div>
      <div className="bg-card border border-border rounded-lg p-4 space-y-3" data-testid="sku-header">
        <div className="flex flex-wrap items-start gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="sku-item-name">{p.name}</h1>
            <p className="metric text-primary font-bold" data-testid="sku-code">{p.sku}{p.item_code ? ` · ${p.item_code}` : ""} <span className="ml-2 align-middle"><Status s={p.status || "active"} /></span></p>
          </div>
          <div className="ml-auto flex flex-wrap gap-1.5" data-testid="sku-actions">
            {actions.map(([label, key, cls]) => (
              <Button key={key} size="sm" variant={cls.includes("bg-") ? "default" : "outline"} className={`h-8 text-xs font-bold uppercase ${cls}`} data-testid={`sku-action-${key}`}
                onClick={() => (key === "ledger" ? setTab("Stock Details") : setDlg(key))}>{label}</Button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="sku-header-buckets">
          {HEAD.map(([k, label, cls]) => (
            <div key={k} className="bg-background/60 border border-border rounded-md p-2" data-testid={`sku-head-${k}`}>
              <p className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">{label}</p>
              <p className={`metric text-lg font-extrabold ${cls || ""}`}>{n(s[k])}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-1.5 flex-wrap border-b border-border pb-2" data-testid="sku-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`sku-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-widest transition-colors ${tab === t ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent"}`}>{t}</button>
        ))}
      </div>

      {tab === "Item Details" && <ItemDetailsTab p={p} perms={perms} />}
      {tab === "Stock Details" && <StockDetailsTab d={d} />}
      {tab === "Warehouses / Godowns / Factories" && <WarehousesTab d={d} onPutaway={perms.putaway ? () => setDlg("putaway") : null} />}
      {tab === "Party-wise Report" && <PartyReportTab sku={p.sku} perms={perms} />}
      {tab === "Party-wise Prices" && <PartyPricesTab d={d} onChanged={load} />}
      {tab === "Orders · Purchase · Production · QC" && <OperationsTab d={d} />}

      {dlg === "edit" && <EditItemDialog d={d} open onClose={() => setDlg(null)} onDone={load} />}
      <AdjustDialog d={d} open={dlg === "adjust"} onClose={() => setDlg(null)} onDone={load} />
      <TransferDialog d={d} open={dlg === "transfer"} onClose={() => setDlg(null)} onDone={load} />
      <PutawayDialog d={d} open={dlg === "putaway"} onClose={() => setDlg(null)} onDone={load} />
      <ArchiveDialog d={d} open={dlg === "archive"} onClose={() => setDlg(null)} onDone={(deleted) => (deleted ? navigate("/stock") : load())} />
    </div>
  );
}
